import { arrayCount } from "common/utilities/core/array";
import { findRangeIntersection } from "common/utilities/core/number";
import type {
  EntitySequence as Sequence,
  SequenceAlign,
  SequenceFeature,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";
import { residuesStringToArray } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

export function removeAllGaps(residues: string) {
  return residues.replace(/[.]/g, "");
}

export function removeGapsFromPosition(residues: string, positionStart?: number, positionEnd?: number) {
  const residuesAsArray = residuesStringToArray(residues);

  return residuesAsArray
    .reduce<string[]>((acc, residue, i) => {
      const newAcc = [...acc];
      if (
        !(
          residue === "." &&
          (positionStart === undefined ? 0 : positionStart) <= i &&
          (positionEnd === undefined ? residuesAsArray.length : positionEnd) >= i
        )
      ) {
        newAcc.push(residue);
      }
      return newAcc;
    }, [])
    .join("");
}

export function removeGapsFromSelectedSequences(
  selection: SequenceSelection,
  alignedSequences: SequenceAlign[],
  sequences: Sequence[]
) {
  const updatedAlignedSequences: SequenceAlign[] = [];
  const updatedSequences: Sequence[] = [];

  sequences.forEach((sequence) => {
    const isSelected = selection.ids.includes(sequence.id);
    const alignedSequence = alignedSequences.find((as) => as.id === sequence.id);

    if (!alignedSequence) {
      updatedSequences.push(sequence);
      return;
    }

    if (isSelected) {
      // Aligned sequence
      const updatedAlignedSequence: SequenceAlign = { ...alignedSequence };

      const selectionPositionStart = selection.positionStart === undefined ? 0 : selection.positionStart;
      // Feature position starts at 1 (selection at 0, logic must adjust)
      const selectionPositionEnd =
        selection.positionEnd === undefined ? alignedSequence.residues.length : selection.positionEnd;

      updatedAlignedSequence.residues = removeGapsFromPosition(
        alignedSequence.residues,
        selectionPositionStart,
        selectionPositionEnd
      );

      updatedAlignedSequences.push(updatedAlignedSequence);

      const numberOfGapsRemoved = alignedSequence.residues.length - updatedAlignedSequence.residues.length;
      const positionOfLastGapRemoved = alignedSequence.residues.slice(0, selectionPositionEnd + 1).lastIndexOf(".");

      // Sequence
      const updatedSequence: Sequence = { ...sequence };

      updatedSequence.features = updatedSequence.features.map((feature) => {
        const updatedFeature: SequenceFeature = { ...feature };

        // Find if the selection is intersecting the feature
        const intersection = findRangeIntersection(
          [updatedFeature.start, updatedFeature.stop],
          [selectionPositionStart, selectionPositionEnd]
        );

        if (intersection) {
          // Extract residues matching the intersection
          const residuesFromSelectionPositionStartToFeatureStart = residuesStringToArray(
            alignedSequence.residues
          ).slice(selectionPositionStart, feature.start);
          const residuesFromSelectionPositionStartToFeatureEnd = residuesStringToArray(alignedSequence.residues).slice(
            selectionPositionStart,
            // The selection might finish within a feature
            selectionPositionEnd > feature.stop ? feature.stop : selectionPositionEnd + 1
          );

          // Shift the feature start position by the number of gaps from the beginning of the selection to the start of the feature
          updatedFeature.start -= arrayCount(residuesFromSelectionPositionStartToFeatureStart, ".");
          // Shift the feature end position by the number of gaps from the beginning of the selection to the end of the feature
          updatedFeature.stop -= arrayCount(residuesFromSelectionPositionStartToFeatureEnd, ".");
        }

        return updatedFeature;
      });

      // Adjust all features starting AFTER the LAST GAP within the selection as they all need to shift as well
      updatedSequence.features = updatedSequence.features.map((feature) => {
        const updatedFeature: SequenceFeature = { ...feature };

        if (positionOfLastGapRemoved && updatedFeature.start > positionOfLastGapRemoved) {
          updatedFeature.start -= numberOfGapsRemoved;
          updatedFeature.stop -= numberOfGapsRemoved;
        }

        return updatedFeature;
      });

      updatedSequences.push(updatedSequence);
    } else {
      updatedAlignedSequences.push(alignedSequence);
      updatedSequences.push(sequence);
    }
  });

  return { updatedAlignedSequences, updatedSequences };
}

/** Ensure each residue from the selection or a given position is a gap */
export function isSelectionGapsOnly(
  selection: SequenceSelection,
  alignedSequences: SequenceAlign[],
  position?: number
) {
  let validSelection = true;

  alignedSequences.forEach((alignedSequence) => {
    const isSelected = selection.ids.includes(alignedSequence.id);

    if (isSelected) {
      if (position !== undefined) {
        const selectedResidue = alignedSequence.residues.slice(position, position + 1);
        if (selectedResidue !== ".") validSelection = false;
      } else if (selection.positionStart !== undefined && selection.positionEnd !== undefined) {
        const selectedResidues = alignedSequence.residues.slice(selection.positionStart, selection.positionEnd + 1);
        if (removeAllGaps(selectedResidues).length !== 0) validSelection = false;
      }
    }
  });

  return validSelection;
}
